import FindReplaceIcon from "@mui/icons-material/FindReplace";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type {ColumnDef} from "@tanstack/react-table";
import ProfilePreviewDialog from "crqlar-react-common/dist/components/profilePreview/ProfilePreviewDialog";
import SurveyInviteButton from "../surveys/share/SurveyInviteButton";
import type {AggregationVoiceNoteListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {VoiceNoteAggregationService} from "crqlar-react-common/dist/services/aggregation/voiceNoteAggregation.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {VoiceNoteService} from "crqlar-react-common/dist/services/voiceNote/voiceNote.service";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useCallback, useMemo, useState} from "react";
import {useSearchParams} from "react-router-dom";
import CommonConfirmDialog from "crqlar-react-common/dist/components/dialog/CommonConfirmDialog";
import {
    ServerTable,
    ServerTableFrame,
    ServerTableProvider,
    ServerTableRailColumns,
    ServerTableRailFullscreen,
    ServerTableRailSearch,
    ServerTableRailSort,
    useServerTable,
} from "../common/serverTable";
import {AllergiesElement} from "../common/uiElements/AllergiesElement";
import {DateElement} from "../common/uiElements/DateElement";
import {AccommodationElement} from "../common/uiElements/ResourceElement";
import {SimpleChipElement} from "../common/uiElements/SimpleChipElement";
import {
    RightSidebarRail,
    RightSidebarRailHeader,
    RightSidebarRailHeaderCenter,
    RightSidebarRailLayout,
} from "../layout/rightSidebar/RightSidebarRail";
import SingleDatePicker from "../common/newDatePicker/SingleDatePicker";
import ReplaceProfileDialog from "./ReplaceProfileDialog";

type VoiceNoteTableRow = AggregationVoiceNoteListItem;

function VoiceNoteGuestCell({
                                voiceNote,
                                onPreview,
                                onReplace,
                            }: {
    voiceNote: VoiceNoteTableRow;
    onPreview: (guestId: string, voiceNoteId: string) => Promise<void>;
    onReplace: (voiceNote: VoiceNoteTableRow) => void;
}) {
    const guest = voiceNote.guest;

    if (!guest) return null;

    return (
        <Stack direction="row" gap={1} alignItems="center" minWidth={0}>
            <ButtonBase
                data-no-row-click
                onClick={() => void onPreview(guest.id, voiceNote.id)}
                sx={{
                    display: "block",
                    minWidth: 0,
                    flex: 1,
                    p: 1,
                    border: "1px solid #E0E0E0",
                    borderRadius: 0.5,
                    textAlign: "left",
                    "&:hover": {backgroundColor: "#F5F5F5"},
                }}
            >
                <Typography noWrap>
                    {guest.displayName ?? ""}
                </Typography>

                <Typography variant="body2" color="text.secondary" noWrap>
                    {guest.email ?? ""}
                </Typography>
            </ButtonBase>

            <IconButton
                data-no-row-click
                aria-label="Replace profile"
                onClick={() => onReplace(voiceNote)}
            >
                <FindReplaceIcon/>
            </IconButton>
        </Stack>
    );
}

const VoiceNoteList = () => {
    const {t} = useLocalize();
    const [searchParams, setSearchParams] = useSearchParams();
    const [currentProfile, setCurrentProfile] = useState<Profile>();
    const [selectedVoiceNote, setSelectedVoiceNote] = useState<VoiceNoteTableRow>();
    const [chosenProfileForReplacement, setChosenProfileForReplacement] = useState<Profile>();
    const [confirmationOpen, setConfirmationOpen] = useState(false);

    const [createdAtDate, setCreatedAtDate] = useState(() => startOfDay(new Date()));

    const handlePreview = useCallback(async (guestId: string, voiceNoteId: string) => {
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.set("voiceNoteId", voiceNoteId);

        setSearchParams(nextSearchParams, {replace: true});

        try {
            setCurrentProfile(await ProfileService.getProfileById(guestId));
        } catch (error) {
            console.error("Could not load voice note guest.", error);
        }
    }, [searchParams, setSearchParams]);

    const handleReplaceStart = useCallback((voiceNote: VoiceNoteTableRow) => {
        setSelectedVoiceNote(voiceNote);
    }, []);

    const columns = useMemo<ColumnDef<VoiceNoteTableRow>[]>(() => [
        {
            id: "guest",
            header: t("Guest"),
            accessorFn: voiceNote => voiceNote.guest?.displayName ?? "",
            cell: ({row}) => (
                <VoiceNoteGuestCell
                    voiceNote={row.original}
                    onPreview={handlePreview}
                    onReplace={handleReplaceStart}
                />
            ),
            size: 320,
            meta: {defaultActive: true},
        },
        {
            id: "room",
            header: t("Room"),
            accessorFn: voiceNote => voiceNote.guest?.roomId,
            cell: ({row}) => {
                const roomId = row.original.guest?.roomId;

                return roomId
                    ? <AccommodationElement name={roomId} color="#55C3C1"/>
                    : null;
            },
            size: 180,
            meta: {defaultActive: true},
        },
        {
            id: "originalTranscription",
            header: t("Transcription"),
            accessorFn: voiceNote => voiceNote.originalTranscription,
            cell: ({row}) => <Box>{row.original.originalTranscription ?? ""}</Box>,
            size: 360,
            meta: {defaultActive: true, isServerSortable: true, grow: true},
        },
        {
            id: "allergies",
            header: t("Allergies"),
            accessorFn: voiceNote => voiceNote.extractedAllergies,
            cell: ({row}) => <AllergiesElement allergies={row.original.extractedAllergies}/>,
            size: 230,
            meta: {defaultActive: true},
        },
        {
            id: "preferences",
            header: t("Preferences"),
            accessorFn: voiceNote => voiceNote.extractedPreferences,
            cell: ({row}) => <SimpleChipElement values={row.original.extractedPreferences}/>,
            size: 230,
            meta: {defaultActive: true},
        },
        {
            id: "insertDate",
            header: t("Date"),
            accessorFn: voiceNote => voiceNote.insertDate,
            cell: ({row}) => <DateElement date={row.original.insertDate}/>,
            size: 190,
            meta: {defaultActive: true, isServerSortable: true},
        },
        {
            id: "englishTranscription",
            header: t("Transcription - English"),
            accessorFn: voiceNote => voiceNote.englishTranscription,
            cell: ({row}) => <Box>{row.original.englishTranscription ?? ""}</Box>,
            size: 360,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "userEmailSource",
            header: t("Created By"),
            accessorFn: voiceNote => voiceNote.userEmailSource,
            cell: ({row}) => <Box>{row.original.userEmailSource ?? ""}</Box>,
            size: 220,
            meta: {defaultActive: false, isServerSortable: true},
        },
        {
            id: "id",
            header: t("ID"),
            accessorFn: voiceNote => voiceNote.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true},
        },
    ], [handlePreview, handleReplaceStart, t]);

    const createdAt = useMemo(() => ({
        start: startOfDay(createdAtDate),
        end: endOfDay(createdAtDate),
    }), [createdAtDate]);

    const controller = useServerTable<VoiceNoteTableRow>({
        tableId: "voice-note-list",
        columns,
        getRowId: voiceNote => voiceNote.id!,
        initialSorting: [{id: "id", desc: true}],
        queryDependencies: [createdAtDate.getTime()],
        queryFn: async ({pagination, searchQuery, sorting}) => {
            const page = await VoiceNoteAggregationService.listVoiceNotes({
                query: searchQuery || undefined,
                createdAt,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined,
                },
            });

            return {
                content: page.content,
                totalElements: page.totalElements,
            };
        },
    });

    const handleReplaceProfile = async () => {
        if (!selectedVoiceNote || !chosenProfileForReplacement?.id) return;

        setConfirmationOpen(false);

        try {
            const [oldProfile, voiceNote] = await Promise.all([
                selectedVoiceNote.guest
                    ? ProfileService.getProfileById(selectedVoiceNote.guest.id)
                    : Promise.resolve(undefined),
                VoiceNoteService.getVoiceNoteById(selectedVoiceNote.id),
            ]);

            if (oldProfile?.preferences?.length) {
                await ProfileService.updateProfile({
                    ...oldProfile,
                    preferences: oldProfile.preferences.filter(
                        preference => preference.voiceNoteId !== selectedVoiceNote.id,
                    ),
                });
            }

            await VoiceNoteService.assignVoiceNote({
                voiceNoteId: selectedVoiceNote.id,
                profileId: chosenProfileForReplacement.id,
            });

            await VoiceNoteService.updateVoiceNote({
                ...voiceNote,
                guestId: chosenProfileForReplacement.id,
            });

            setSelectedVoiceNote(undefined);
            setChosenProfileForReplacement(undefined);
            await controller.query.refetch();
        } catch (error) {
            console.error("Could not replace voice note profile.", error);
        }
    };

    return (
        <>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <RightSidebarRailLayout>
                        <RightSidebarRailHeader>
                            <RightSidebarRailHeaderCenter>
                                <SingleDatePicker
                                    value={createdAtDate}
                                    onChange={setCreatedAtDate}
                                    size="medium"
                                />
                            </RightSidebarRailHeaderCenter>
                        </RightSidebarRailHeader>

                        <ServerTable/>

                        <RightSidebarRail>
                            <ServerTableRailSearch/>
                            <ServerTableRailSort/>
                            <ServerTableRailFullscreen/>
                            <ServerTableRailColumns/>
                        </RightSidebarRail>
                    </RightSidebarRailLayout>
                </ServerTableFrame>
            </ServerTableProvider>

            <ProfilePreviewDialog
                open={!!currentProfile}
                onClose={() => setCurrentProfile(undefined)}
                profile={currentProfile}
                extraActions={<SurveyInviteButton profileId={currentProfile?.id}/>}
                setProfile={profile => {
                    setCurrentProfile(profile);
                    void controller.query.refetch();
                }}
            />

            <ReplaceProfileDialog
                open={!!selectedVoiceNote}
                onClose={() => {
                    setSelectedVoiceNote(undefined);
                    setChosenProfileForReplacement(undefined);
                }}
                onChosenProfile={profile => {
                    if (!profile) return;

                    setChosenProfileForReplacement(profile);
                    setConfirmationOpen(true);
                }}
            />

            <CommonConfirmDialog
                open={confirmationOpen}
                onClose={() => setConfirmationOpen(false)}
                onConfirm={() => void handleReplaceProfile()}
            />
        </>
    );
};

export default VoiceNoteList;
